import { Link } from "react-router-dom";
import { useFeatures } from "@/hooks/queries/use-features";
import { useTasks } from "@/hooks/queries/use-tasks";
import type { Feature, Status } from "@/lib/api/types";
import type { IdeaStageResult } from "./stage";

/**
 * Owner checks: parts the planner leaves for a person ("OWNER: launch
 * sign-off checklist"). The runner never does them, so once the build is
 * finished they are what's left, and the pages say it's the owner's turn
 * instead of "Shipped".
 */

const OWNER_TITLE = /^owner\b/i;

function isOpen(status: Status | undefined): boolean {
  const category = status?.category;
  return category !== "completed" && category !== "canceled";
}

/** Owner parts that aren't done yet. */
export function openOwnerParts(features: Feature[]): Feature[] {
  return features.filter((f) => OWNER_TITLE.test(f.title.trim()) && isOpen(f.status));
}

/** How many checks are left: open tasks across the parts, or one per part with no tasks. */
export function ownerChecksLeft(parts: Feature[]): number {
  return parts.reduce((sum, f) => {
    const total = f._count?.tasks ?? 0;
    return sum + (total > 0 ? Math.max(total - (f.completedTaskCount ?? 0), 0) : 1);
  }, 0);
}

/**
 * Whether it's the owner's turn: nothing running, nothing else asked, and every
 * part done except the open owner parts.
 */
export function isOwnerTurn(
  result: IdeaStageResult,
  running: boolean,
  parts: { done: number; total: number; owner: number; checksLeft: number }
): boolean {
  if (result.closed || result.needsYou || running || parts.checksLeft === 0) return false;
  if (parts.done + parts.owner < parts.total) return false;
  return result.stage === "done" || result.stage === "build";
}

export function ownerTurnTitle(checksLeft: number): string {
  return `Your turn: ${String(checksLeft)} ${checksLeft === 1 ? "check" : "checks"} left`;
}

export const OWNER_TURN_BODY =
  "The build is finished. These checks need you; mark each task done when you've done it.";

/** "4 of 5", or "4 of 5 (1 is yours)" while owner parts are open. */
export function partsDoneText(done: number, total: number, ownerParts: number): string {
  const base = `${String(done)} of ${String(total)}`;
  return ownerParts > 0
    ? `${base} (${String(ownerParts)} ${ownerParts === 1 ? "is" : "are"} yours)`
    : base;
}

/** The open owner parts of a blueprint and how many checks are left. */
export function useOwnerChecks(epicId: string) {
  const { data } = useFeatures({ epicId, limit: 100 });
  const parts = epicId ? openOwnerParts(data?.pages.flatMap((p) => p.data) ?? []) : [];
  return { parts, checksLeft: ownerChecksLeft(parts) };
}

function OwnerPartTasks({ feature }: { feature: Feature }) {
  const { data, isLoading } = useTasks({ featureId: feature.id, limit: 100 });
  const tasks = (data?.pages.flatMap((p) => p.data) ?? []).filter((t) => isOpen(t.status));
  if (isLoading) return <div className="h-11 animate-pulse rounded-lg bg-muted" />;
  const items =
    tasks.length > 0
      ? tasks.map((t) => ({ id: t.id, to: `/tasks/${t.id}`, ref: t.identifier, title: t.title }))
      : [
          {
            id: feature.id,
            to: `/features/${feature.id}`,
            ref: feature.identifier,
            title: feature.title,
          },
        ];
  return (
    <>
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            className="flex min-h-11 items-center gap-3 rounded-lg px-2 hover:bg-amber-100/60 dark:hover:bg-amber-500/10"
          >
            <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
              {item.ref}
            </span>
            <span className="min-w-0 flex-1 text-sm text-foreground">
              {item.title.replace(/^owner:\s*/i, "")}
            </span>
          </Link>
        </li>
      ))}
    </>
  );
}

/** The owner's open checks, each linking to its task. */
export function OwnerChecks({ parts }: { parts: Feature[] }) {
  return (
    <ul aria-label="Your checks" className="-mx-2 mt-1 flex flex-col">
      {parts.map((f) => (
        <OwnerPartTasks key={f.id} feature={f} />
      ))}
    </ul>
  );
}
